import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardActions,
  Grid,
  Stack,
} from '@mui/material';
import { paymentService } from '@services/payment.service.ts';
import { SubscriptionStatus, Transaction, SubscriptionPlan, PaymentOption } from '@/types/payment';
import { toastService } from '@services/toast.service.ts';
import { format, isValid } from 'date-fns';
import { useAppSelector } from '@store/index.ts';
import { selectUser } from '@store/auth/authSlice.ts';
import { PAYMENT_CONFIG } from '../../config.ts';
import { PaymentModal } from '@components/payment/PaymentModal.tsx';
import { TabIndices } from '../../common/i-doc/i-doc.registry.ts';

export const SubscriptionSettingsTab: React.FC = () => {
  const user = useAppSelector(selectUser);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Filter State
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  
  // Modal State
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [statusData, plansResponse, optionsResponse] = await Promise.all([
        paymentService.getMySubscription(),
        paymentService.getPlans(),
        paymentService.getPaymentOptions(),
      ]);
      setStatus(statusData);
      setPaymentOptions(optionsResponse.statuses);
      
      if (plansResponse && Array.isArray(plansResponse.plans)) {
        setPlans(plansResponse.plans);
      } else {
        console.warn('Plans response structure mismatch:', plansResponse);
        setPlans([]);
      }

      // Initial load of transactions (default filter usually implies all or specific logic, 
      // but here we load based on selectedStatus which is initially null)
      await loadTransactions();

    } catch (error) {
      console.error('Failed to load subscription data', error);
      toastService.error('Не вдалося завантажити дані підписки');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (statusFilter?: string | null) => {
    try {
      // If statusFilter is provided, use it. If it's explicitly null, send undefined to service.
      // If not provided (undefined), use current selectedStatus.
      const filter = statusFilter !== undefined ? statusFilter : selectedStatus;
      
      const transactionsData = await paymentService.getMyTransactions(20, 0, filter || undefined);
      setTransactions(transactionsData.items);
    } catch (error) {
      console.error('Failed to load transactions', error);
      toastService.error('Не вдалося завантажити історію платежів');
    }
  };

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const handleStatusFilterChange = (newStatus: string | null) => {
    // If clicking the same status, deselect it (toggle)
    const nextStatus = selectedStatus === newStatus ? null : newStatus;
    setSelectedStatus(nextStatus);
    loadTransactions(nextStatus);
  };

  const handleCancelSubscription = async () => {
    if (!user) return;
    if (!window.confirm('Ви впевнені, що хочете скасувати автоматичне подовження підписки?')) {
      return;
    }

    setProcessing(true);
    try {
      await paymentService.cancelSubscription();
      toastService.success('Підписку успішно скасовано');
      await loadData();
    } catch (error) {
      console.error('Failed to cancel subscription', error);
      toastService.error('Не вдалося скасувати підписку');
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = () => {
    loadData();
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'dd.MM.yyyy HH:mm') : '-';
  };

  const formatDateOnly = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'dd.MM.yyyy') : '-';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'declined':
      case 'expired':
      case 'payment_failed':
        return 'error';
      case 'created':
      case 'pending':
        return 'warning';
      case 'refunded':
      case 'voided':
        return 'info';
      default:
        return 'default';
    }
  };

  const renderTransactionsTable = (data: Transaction[]) => (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Дата</TableCell>
            <TableCell>Сума</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Тип</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length > 0 ? (
            data.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>{formatDate(transaction.createdAt)}</TableCell>
                <TableCell>{transaction.amount} {transaction.currency}</TableCell>
                <TableCell>
                  <Chip 
                    label={transaction.statusLabel || transaction.status} 
                    color={getStatusColor(transaction.status)} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{transaction.typeLabel || transaction.type}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">Записів не знайдено</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Helper to find current plan details
  const getCurrentPlanDetails = () => {
    if (!status || !status.plan || plans.length === 0) return null;
    
    // Try exact match first
    let found = plans.find(p => p.name === status.plan);
    
    // Try case-insensitive
    if (!found) {
      found = plans.find(p => p.name.toLowerCase() === status.plan?.toLowerCase());
    }

    // Try by code (if status.plan matches code)
    if (!found) {
       found = plans.find(p => p.code === status.plan);
    }

    return found;
  };

  const currentPlan = getCurrentPlanDetails();
  
  // Determine next payment amount
  let nextPaymentAmount = null;
  if (currentPlan && status?.billingCycle) {
    // Try flat structure first, then nested
    if (status.billingCycle === 'month') {
      nextPaymentAmount = currentPlan.priceMonth || currentPlan.prices?.month;
    } else {
      nextPaymentAmount = currentPlan.priceYear || currentPlan.prices?.year;
    }
  }

  // Debug logging
  useEffect(() => {
    if (status) {
      console.log('Subscription Debug:', {
        statusPlan: status.plan,
        plansCount: plans.length,
        currentPlanName: currentPlan?.name,
        nextPaymentAmount,
        isAutoRenewal: status.isAutoRenewal,
        isActive: status.isActive,
        billingCycle: status.billingCycle
      });
    }
  }, [status, plans, currentPlan, nextPaymentAmount]);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ pb: 4 }} data-i-doc={TabIndices.SETTINGS_SUBSCRIPTION}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Підписка</Typography>
        {PAYMENT_CONFIG.enableSandbox && (
          <Typography variant="caption" sx={{ color: 'warning.main' }}>
            Sandbox Mode
          </Typography>
        )}
      </Box>
      
      {/* Status Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Статус підписки</Typography>
        {status ? (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1" component="span" sx={{ fontWeight: 'bold' }}>
                    Статус:
                  </Typography>
                  <Chip 
                    label={status.isActive ? (status.isTrial ? 'Пробний період' : 'Активна') : 'Неактивна'} 
                    color={status.isActive ? (status.isTrial ? 'info' : 'success') : 'default'} 
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  <strong>Тариф:</strong> {status.plan || 'Немає'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  <strong>Діє до:</strong> {formatDateOnly(status.nextPaymentDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  <strong>Автоподовження:</strong> {status.isAutoRenewal ? 'Так' : 'Ні'}
                </Typography>
              </Grid>
              
              {/* Extended Info */}
              {status.amount && (
                 <Grid item xs={12} sm={6}>
                    <Typography variant="body1">
                      <strong>Остання оплата:</strong> {status.amount} UAH
                      {status.lastPaymentDate && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          ({formatDateOnly(status.lastPaymentDate)})
                        </Typography>
                      )}
                    </Typography>
                 </Grid>
              )}
              
              {status.billingCycle && (
                 <Grid item xs={12} sm={6}>
                    <Typography variant="body1">
                      <strong>Цикл:</strong> {status.billingCycle === 'month' ? 'Місячний' : 'Річний'}
                    </Typography>
                 </Grid>
              )}

              {/* Show Next Payment if AutoRenewal is ON OR if it's a Trial (even if AutoRenewal is technically off, but usually trial implies renewal) */}
              {status.isActive && status.isAutoRenewal && nextPaymentAmount && (
                <Grid item xs={12} sm={6}>
                   <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                     <strong>Наступний платіж:</strong> {nextPaymentAmount} {currentPlan?.currency || 'UAH'}
                     <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                       ({formatDateOnly(status.nextPaymentDate)})
                     </Typography>
                   </Typography>
                </Grid>
              )}
            </Grid>

            {status.isActive && status.isAutoRenewal && (
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="outlined" 
                  color="error" 
                  onClick={handleCancelSubscription}
                  disabled={processing}
                >
                  Скасувати підписку
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <Alert severity="info">Інформація про підписку відсутня</Alert>
        )}
      </Paper>

      {/* Plans Section (if not active or upgrade needed) */}
      <Typography variant="h6" gutterBottom>Доступні тарифи</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {plans.map((plan) => (
          <Grid item xs={12} sm={6} md={4} key={plan.id}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              border: status?.plan === plan.name ? '2px solid #4caf50' : 'none'
            }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="div" gutterBottom>
                  {plan.name}
                </Typography>
                
                {/* Display prices based on unified structure */}
                {plan.priceMonth && plan.priceYear ? (
                   <Box>
                     <Typography variant="h4" color="primary" gutterBottom>
                       {plan.priceMonth} {plan.currency}
                       <Typography variant="caption" color="text.secondary" component="span"> / міс</Typography>
                     </Typography>
                     <Typography variant="body2" color="text.secondary">
                       або {plan.priceYear} {plan.currency} / рік
                     </Typography>
                   </Box>
                ) : (
                   <Typography variant="h4" color="primary" gutterBottom>
                     {plan.price} {plan.currency}
                   </Typography>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {plan.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="large" 
                  fullWidth 
                  variant={status?.plan === plan.name ? "outlined" : "contained"}
                  disabled={status?.plan === plan.name || processing}
                  onClick={() => setIsPaymentModalOpen(true)}
                >
                  {status?.plan === plan.name ? 'Поточний план' : 'Змінити план'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* History Section */}
      <Typography variant="h6" gutterBottom>Історія платежів</Typography>
      
      {/* Filter Chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Chip 
          label="Всі" 
          onClick={() => handleStatusFilterChange(null)}
          color={selectedStatus === null ? 'primary' : 'default'}
          variant={selectedStatus === null ? 'filled' : 'outlined'}
          clickable
        />
        {paymentOptions
          .filter(option => option.count > 0) // Hide options with 0 count
          .map((option) => (
          <Chip
            key={option.value}
            label={`${option.label} (${option.count})`}
            onClick={() => handleStatusFilterChange(option.value)}
            color={selectedStatus === option.value ? 'primary' : 'default'}
            variant={selectedStatus === option.value ? 'filled' : 'outlined'}
            clickable
          />
        ))}
      </Stack>
      
      {renderTransactionsTable(transactions)}

      {/* Payment Modal */}
      <PaymentModal 
        open={isPaymentModalOpen} 
        onClose={() => setIsPaymentModalOpen(false)} 
        canClose={true}
        onSuccess={handlePaymentSuccess}
      />
    </Box>
  );
};
